import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetPermissionsQuery } from '@api/permissions';
import { useGetInternalRolesPermissionsQuery, useGetInternalRolesQuery } from '@api/roles';
import { useGetCurrentUserQuery } from '@api/users';
import { selectOrganisationContextId } from '@selectors/appSelector';
import { selectUserId } from '@selectors/authSelector';
import { CustomerUserManagementPermission } from '@shared/enums/CustomerUserManagementPermission';
import { InternalRoleType } from '@shared/models/userModels';

const manageUserPermissionIds = Object.values(CustomerUserManagementPermission);

export const useCustomerPermissions = () => {
  const userId = useSelector(selectUserId);
  const organisationId = useSelector(selectOrganisationContextId);
  const userQuery = useGetCurrentUserQuery(
    { organisationId: organisationId! },
    { skip: !userId || !organisationId },
  );
  const rolesQuery = useGetInternalRolesQuery(undefined, { skip: !userId });
  const rolePermissionsQuery = useGetInternalRolesPermissionsQuery(undefined, { skip: !userId });
  const permissionsQuery = useGetPermissionsQuery(undefined, { skip: !userId });
  const { currentData: user } = userQuery;
  const { data: roles = [] } = rolesQuery;
  const { data: rolePermissions = [] } = rolePermissionsQuery;
  const { data: permissions = [] } = permissionsQuery;
  const queries = [userQuery, rolesQuery, rolePermissionsQuery, permissionsQuery];
  // On an organisation switch RTK Query can be fetching with isLoading=false.
  // Wait for the active scope instead of treating missing data as a denial.
  const isLoading = Boolean(userId && organisationId) && queries.some(
    (query) => query.isLoading || query.isFetching || query.isUninitialized,
  );
  const isError = queries.some((query) => query.isError);

  return useMemo(() => {
    const activeMembership = !isLoading && !isError
      ? user?.organisations.find(({ id }) => id === organisationId)
      : undefined;
    const activeRoleId = activeMembership?.admin_role;
    const isCustomerPortalRole = roles.some(
      (role) => role.id === activeRoleId && role.type === InternalRoleType.ClientPortal,
    );
    const permissionIds = new Set(
      isCustomerPortalRole
        ? rolePermissions.find(({ id }) => id === activeRoleId)?.permissions ?? []
        : [],
    );
    const permissionNames = new Set(
      permissions.filter(({ id }) => permissionIds.has(id)).map(({ name }) => name),
    );

    return {
      isLoading,
      permissionIds,
      permissionNames,
      hasAnyManageUsersPermission: manageUserPermissionIds.some((id) => permissionIds.has(id)),
      canAddUsers: permissionIds.has(CustomerUserManagementPermission.AddUser),
      canEditUsers: permissionIds.has(CustomerUserManagementPermission.EditUser),
      canDeleteUsers: permissionIds.has(CustomerUserManagementPermission.DeleteUser),
      canManageRoles: permissionIds.has(CustomerUserManagementPermission.ManageRoles),
      activeMembership,
      user,
      userId,
      organisationId,
      roles,
      rolePermissions,
    };
  }, [isError, isLoading, organisationId, permissions, rolePermissions, roles, user, userId]);
};
